// OfflineConfirmDialog – warns when some selected devices are offline or busy before sending a command.

import React, { JSX } from 'react';
import { PortaledLayer } from '../../shared/PortaledLayer';

// ---- Types ----

type OfflineConfirmDialogProps = {
  open: boolean;
  offlineIds: string[];
  blockedIds: string[];
  onlineIds: string[];
  onCancel: () => void;
  onProceed: () => void;
};

// ---- Component ----

export function OfflineConfirmDialog(props: OfflineConfirmDialogProps): JSX.Element | null {
  const { open, offlineIds, blockedIds, onlineIds, onCancel, onProceed } = props;

  if (!open) return null;

  const totalRemoved = offlineIds.length + blockedIds.length;

  return (
    <PortaledLayer>
      <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4 dark:bg-black/70">
        <div className="w-full max-w-md overflow-hidden rounded-lg border border-gray-200 bg-white text-sm text-gray-800 shadow-xl dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
        <div className="border-b border-gray-200 bg-gray-50 px-4 py-3 font-semibold text-gray-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100">
          Some devices are not eligible
        </div>

        {/* Explain who gets removed and who stays */}
        <div className="space-y-2 px-4 py-3">
          <p>
            {totalRemoved} devices will be removed because they're not eligible
            to receive a new command right now.
          </p>

          {blockedIds.length > 0 && (
            <>
              <p className="text-xs text-gray-700 dark:text-slate-300">
                {blockedIds.length} are busy (PENDING / IN_PROGRESS / ACKED)
              </p>
              <div className="max-h-32 overflow-y-auto rounded border border-gray-200 bg-gray-50 px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                {blockedIds.map((id) => (
                  <div key={id}>{id}</div>
                ))}
              </div>
            </>
          )}

          {offlineIds.length > 0 && (
            <>
              <p className="text-xs text-gray-700 dark:text-slate-300">
                {offlineIds.length} are offline
              </p>
              <div className="max-h-32 overflow-y-auto rounded border border-gray-200 bg-gray-50 px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                {offlineIds.map((id) => (
                  <div key={id}>{id}</div>
                ))}
              </div>
            </>
          )}

          {onlineIds.length > 0 ? (
            <p className="text-xs text-gray-600 dark:text-slate-400">
              This will target {onlineIds.length} eligible online devices.
            </p>
          ) : (
            <p className="text-xs text-red-600 dark:text-red-300">
              No eligible online devices remain.
            </p>
          )}
        </div>

        {/* Cancel or proceed with online-only */}
        <div className="flex justify-end gap-2 border-t border-gray-200 bg-gray-50 px-4 py-3 text-xs dark:border-slate-700 dark:bg-slate-800">
          <button
            type="button"
            className="rounded border border-gray-300 bg-white px-3 py-1 text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-4 py-1 rounded bg-blue-600 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
            disabled={onlineIds.length === 0}
            onClick={onProceed}
          >
            Proceed
          </button>
        </div>
      </div>
      </div>
    </PortaledLayer>
  );
}
